/**
 * Explicit success/failure without exceptions.
 *
 * Used on every path where failure is an expected outcome rather than a bug:
 * validation, storage writes, quota, migration, and restore. A caller cannot
 * read `.value` without narrowing on `.ok`, so a failure cannot be ignored by
 * accident — which is the property `docs/privacy-and-storage.md` requires when
 * it says a failed write must never be reported as success.
 */
export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E }

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value }
}

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error }
}

export function isOk<T, E>(result: Result<T, E>): result is { ok: true; value: T } {
  return result.ok
}

/** Collect a list of results, failing on the first error. */
export function allOk<T, E>(results: readonly Result<T, E>[]): Result<T[], E> {
  const values: T[] = []
  for (const result of results) {
    if (!result.ok) return result
    values.push(result.value)
  }
  return ok(values)
}
